import { addMinutes, isBefore, isEqual, roundToNearestMinutes, subMinutes } from 'date-fns';
import moment from 'moment';
import { DEFAULT_EVENTS_PAGE_TIME_FORMAT, DEFAULT_TIME_FORMAT, MILLISEC_MINIMUM_RECONSTRUCTED_RESOLUTION, MINIMUM_RECONSTRUCTED_RESOLUTION, PERIOD_SELECTOR_MODES, TIMESPAN_OPTIONS } from 'Utils/constants';
const TWO_WEEKS = 14 * 24;
export const parseFormatDate = (date, format) => date ? moment(date).format(format ?? DEFAULT_TIME_FORMAT) : '';
export const parseFormatDateForEventsPage = (date, format) => date ? moment(date).format(format ?? DEFAULT_EVENTS_PAGE_TIME_FORMAT) : '';
export const parseFormatDateWithAgo = (date, format) => {
    if (!date)
        return '';
    const parsedDate = moment(date);
    if (!parsedDate.isValid()) {
        return date;
    }
    return `${parsedDate.fromNow()} ${parseFormatDate(date, format)}`;
};
export const formatTimestampToISO = (date) => {
    if (!date)
        return '';
    try {
        return new Date(date).toISOString();
    }
    catch (error) {
        console.error(error);
        return 'Invalid Date';
    }
};
export const formatPeriodSelectionToISO = (period) => {
    if (!period)
        return null;
    const { start, end } = period;
    return {
        start: formatTimestampToISO(start),
        end: formatTimestampToISO(end)
    };
};
export const getValidSecondsPrecisionTimestamp = (timestamp) => {
    const formattedTimestamp = formatTimestampToISO(timestamp);
    if (!formattedTimestamp || formattedTimestamp === 'Invalid Date')
        return null;
    return formattedTimestamp.replace(/\.\d{3}Z$/, 'Z') || null;
};
export const getPeriodURLParam = (period) => {
    const from = period?.startDate ? getValidSecondsPrecisionTimestamp(period.startDate) : null;
    const to = period?.endDate ? getValidSecondsPrecisionTimestamp(period.endDate) : null;
    return from && to ? `&from=${from}&to=${to}` : '';
};
export const makeCustomPeriod = (startDate, endDate) => ({
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    label: TIMESPAN_OPTIONS.CUSTOM,
    type: PERIOD_SELECTOR_MODES.CUSTOM
});
export const makePeriodFromDateToNow = (firstEventStartTimestamp) => {
    const now = new Date();
    const startDate = new Date(firstEventStartTimestamp);
    startDate.setHours(startDate.getHours() - TWO_WEEKS);
    return makeCustomPeriod(startDate, now);
};
export const makeCustomPeriodFromEvent = (event) => {
    const now = new Date();
    const startDate = new Date(event?.start_timestamp);
    startDate.setHours(startDate.getHours() - TWO_WEEKS);
    let endDate;
    if (event?.end_timestamp) {
        endDate = new Date(event?.end_timestamp);
        endDate.setHours(endDate.getHours() + TWO_WEEKS);
        if (endDate > now)
            endDate = now;
    }
    else {
        endDate = now;
    }
    return makeCustomPeriod(startDate, endDate);
};
export const makeCustomPeriodFromVersion = (version) => {
    const { model: { version_start: versionStart, version_end: versionEnd } } = version;
    const now = new Date();
    const startDate = new Date(versionStart);
    startDate.setHours(startDate.getHours() - TWO_WEEKS);
    let endDate;
    if (versionEnd) {
        endDate = new Date(versionEnd);
        endDate.setHours(endDate.getHours() + TWO_WEEKS);
        if (endDate > now)
            endDate = now;
    }
    else {
        endDate = now;
    }
    return makeCustomPeriod(startDate, endDate);
};
export const getXDomainFromPeriod = (period) => period && period.startDate && period.endDate
    ? [new Date(period.startDate), new Date(period.endDate)]
    : null;
export const adjustDateToMinimumResolution = (date, mathMethod = Math.round, minResolution = MILLISEC_MINIMUM_RECONSTRUCTED_RESOLUTION) => mathMethod(Number(new Date(date)) / minResolution) * minResolution;
export const getDate = (date) => new Date(date);
export const roundDateToMinimumResolution = (date, minResolution = MINIMUM_RECONSTRUCTED_RESOLUTION, roundingMethod = 'floor') => roundToNearestMinutes(date, { nearestTo: minResolution, roundingMethod });
export const makeRoundSelectedPeriod = (period) => {
    try {
        const { startDate, endDate } = period;
        let start;
        let end;
        if (startDate === null && endDate === null) {
            start = null;
            end = null;
        }
        else {
            start = new Date(adjustDateToMinimumResolution(startDate, Math.floor, MILLISEC_MINIMUM_RECONSTRUCTED_RESOLUTION));
            end = new Date(adjustDateToMinimumResolution(endDate, Math.ceil, MILLISEC_MINIMUM_RECONSTRUCTED_RESOLUTION));
            if (start && (isBefore(end, start) || isEqual(end, start) || endDate === null)) {
                end = addMinutes(start, MINIMUM_RECONSTRUCTED_RESOLUTION);
            }
            if (end && startDate === null) {
                start = subMinutes(end, MINIMUM_RECONSTRUCTED_RESOLUTION);
            }
        }
        return {
            ...period,
            startDate: start?.toISOString() || null,
            endDate: end?.toISOString() || null
        };
    }
    catch (error) {
        console.error(error);
        return {
            ...period,
            startDate: null,
            endDate: null
        };
    }
};
export const makeRoundTimeWindowPeriod = (period) => {
    try {
        const { start, end } = period;
        let timeWindowStart = start;
        let timeWindowEnd = end;
        if (start === '' && end === '') {
            timeWindowStart = '';
            timeWindowEnd = '';
        }
        else {
            timeWindowStart = start
                ? new Date(adjustDateToMinimumResolution(timeWindowStart, Math.floor, MILLISEC_MINIMUM_RECONSTRUCTED_RESOLUTION)).toISOString()
                : '';
            timeWindowEnd = end
                ? new Date(adjustDateToMinimumResolution(timeWindowEnd, Math.ceil, MILLISEC_MINIMUM_RECONSTRUCTED_RESOLUTION)).toISOString()
                : '';
            if (timeWindowStart &&
                (isBefore(timeWindowEnd, timeWindowStart) || isEqual(timeWindowEnd, timeWindowStart))) {
                timeWindowStart = subMinutes(timeWindowEnd, MINIMUM_RECONSTRUCTED_RESOLUTION).toISOString();
            }
        }
        return {
            ...period,
            start: timeWindowStart || '',
            end: timeWindowEnd || ''
        };
    }
    catch (error) {
        console.error(error);
        return {
            ...period,
            start: '',
            end: ''
        };
    }
};
